import { useState, useEffect, type FormEvent, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { useAuthStore } from '@/stores/authStore'
import { EyeIcon, EyeSlashIcon, ChevronDownIcon, ChevronUpIcon, CircleStackIcon, LockClosedIcon, BoltIcon } from '@heroicons/react/24/outline'
import { notify } from '@/utils/notify'
import { handleGoogleSSO } from '@/utils/ssoAuth'

// Feature item component for cleaner code
function FeatureItem({ icon, title, description }: { icon: ReactNode; title: string; description: string }) {
  return (
    <div className="flex items-start space-x-4">
      <div className="flex-shrink-0">
        <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-zinc-800 border border-zinc-700 text-indigo-400">
          {icon}
        </div>
      </div>
      <div className="text-left">
        <h3 className="text-sm font-medium text-zinc-200 mb-1">{title}</h3>
        <p className="text-sm text-zinc-500 leading-relaxed">{description}</p>
      </div>
    </div>
  )
}

export function LoginPage() {
  const shouldAutoFill = import.meta.env.VITE_DEV_PUT_DEFAULT_CREDENTIALS === 'true'
  const defaultUsername = shouldAutoFill ? (import.meta.env.VITE_DEV_DEFAULT_USERNAME || 'admin') : ''
  const defaultPassword = shouldAutoFill ? (import.meta.env.VITE_DEV_DEFAULT_PASSWORD || 'password123') : ''

  const [username, setUsername] = useState<string>(defaultUsername)
  const [password, setPassword] = useState<string>(defaultPassword)
  const [showPassword, setShowPassword] = useState(false)
  const [showTraditionalLogin, setShowTraditionalLogin] = useState(false)
  const [showLoginButton, setShowLoginButton] = useState(shouldAutoFill)
  const [keyBuffer, setKeyBuffer] = useState('')
  const { login, isLoading } = useAuthStore()
  const navigate = useNavigate()

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only listen for keys when the login button is hidden
      if (!showLoginButton) {
        // Keep last 4 characters
        const newBuffer = (keyBuffer + e.key).slice(-4)
        setKeyBuffer(newBuffer)

        if (newBuffer === 'show') {
          setShowLoginButton(true)
          // Reset buffer
          setKeyBuffer('')
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [keyBuffer, showLoginButton])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!username || !password) {
      notify.error('Please fill in all fields')
      return
    }

    try {
      await login(username, password)
      navigate('/')
    } catch {
      notify.error('Invalid credentials')
    }
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-zinc-950">
      {/* Left Side - Brand Section */}
      <div className="relative lg:flex-1 flex flex-col justify-center items-center p-8 lg:p-12 bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-800 overflow-hidden">
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5" />

        {/* Animated gradient orbs - Subtle in dark mode */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-20 w-80 h-80 bg-indigo-500/10 rounded-full filter blur-3xl animate-blob" />
          <div className="absolute top-1/3 -right-20 w-80 h-80 bg-purple-500/10 rounded-full filter blur-3xl animate-blob animation-delay-2000" />
          <div className="absolute -bottom-20 left-1/4 w-80 h-80 bg-pink-500/10 rounded-full filter blur-3xl animate-blob animation-delay-4000" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-lg text-center lg:text-left">
          {/* Logo */}
          <a
            href="https://mazpin.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mb-8 transition-all duration-300 hover:scale-105 hover:opacity-90"
            aria-label="Visit Mazpin website"
          >
            <img
              src="/images/mazpin/mazpin-logo.png"
              alt="Mazpin Logo"
              className="w-16 h-16 lg:w-20 lg:h-20"
            />
          </a>

          <h1 className="text-3xl lg:text-4xl font-bold text-zinc-100 mb-3 tracking-tight">
            Welcome to Mazpin
          </h1>
          <p className="text-lg text-zinc-400 mb-10 leading-relaxed">
            Your modern web-based database IDE for seamless data management
          </p>

          {/* Feature Highlights */}
          <div className="space-y-5 hidden lg:block">
            <FeatureItem
              icon={<CircleStackIcon className="h-5 w-5" />}
              title="Multi-Database Support"
              description="Seamlessly manage MySQL, PostgreSQL, MongoDB, Redis, and more in one place"
            />
            <FeatureItem
              icon={<BoltIcon className="h-5 w-5" />}
              title="Lightning Fast"
              description="Execute queries and manage data with exceptional performance"
            />
            <FeatureItem
              icon={<LockClosedIcon className="h-5 w-5" />}
              title="Agent-Based Architecture"
              description="Secure database connections through encrypted agents"
            />
          </div>
        </div>

        {/* Bottom decorative line */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-zinc-700 to-transparent" />
      </div>

      {/* Right Side - Login Form */}
      <div className="lg:flex-1 flex items-center justify-center p-6 lg:p-12 bg-zinc-950 relative">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-zinc-900 via-zinc-950 to-zinc-950" />

        <div className="w-full max-w-md relative z-10">
          {/* Login Card */}
          <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-8 lg:p-10 shadow-2xl shadow-black/20">
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-zinc-100 mb-2">
                Sign in
              </h2>
              <p className="text-zinc-500">
                Access your database workspace
              </p>
            </div>

            <div className="space-y-4">
              {/* Google SSO Button */}
              <button
                type="button"
                onClick={() => { handleGoogleSSO() }}
                className="w-full group relative flex justify-center items-center px-6 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-sm font-medium text-zinc-200 hover:bg-zinc-750 hover:border-zinc-600 focus:outline-none focus:ring-0 transition-all duration-200"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </button>

              {/* Divider */}
              {showLoginButton && (
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-zinc-800" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-3 bg-zinc-900 text-zinc-500 text-xs uppercase tracking-wider">
                      Or continue with
                    </span>
                  </div>
                </div>
              )}

              {/* Toggle Traditional Login Button */}
              {showLoginButton && (
                <button
                  type="button"
                  onClick={() => { setShowTraditionalLogin(!showTraditionalLogin) }}
                  className="w-full flex justify-center items-center px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm font-medium focus:outline-none focus:ring-0 transition-all duration-200"
                >
                  Sign in with username and password
                  {showTraditionalLogin ? (
                    <ChevronUpIcon className="ml-2 h-4 w-4" />
                  ) : (
                    <ChevronDownIcon className="ml-2 h-4 w-4" />
                  )}
                </button>
              )}

              {/* Collapsible Traditional Login Form */}
              {showTraditionalLogin && (
                <form className="space-y-5 pt-4 animate-slideDown" onSubmit={(e) => void handleSubmit(e)}>
                  <div>
                    <label htmlFor="username" className="block text-sm font-medium text-zinc-300 mb-2">
                      Username
                    </label>
                    <input
                      id="username"
                      name="username"
                      type="text"
                      autoComplete="username"
                      required
                      value={username}
                      onChange={(e) => { setUsername(e.target.value) }}
                      className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-zinc-100 placeholder-zinc-500 focus:outline-none focus:border-gray-500 dark:focus:border-gray-400 focus:ring-0 transition-all duration-200"
                      placeholder="Enter your username"
                    />
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-zinc-300 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        required
                        value={password}
                        onChange={(e) => { setPassword(e.target.value) }}
                        className="w-full px-4 py-3 pr-12 bg-zinc-800 border border-zinc-700 rounded-xl text-zinc-100 placeholder-zinc-500 focus:outline-none focus:border-gray-500 dark:focus:border-gray-400 focus:ring-0 transition-all duration-200"
                        placeholder="Enter your password"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-4 flex items-center text-zinc-500 hover:text-zinc-300 transition-colors"
                        onClick={() => { setShowPassword(!showPassword) }}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? (
                          <EyeSlashIcon className="h-5 w-5" />
                        ) : (
                          <EyeIcon className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-medium py-3 px-6 rounded-xl focus:outline-none focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Signing in...
                      </span>
                    ) : (
                      'Sign in'
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-sm text-zinc-600">
              Secure authentication powered by{' '}
              <a
                href="https://mazpin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-400 hover:text-indigo-300 transition-colors"
              >
                Mazpin
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Custom Animations */}
      <style>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        /* Custom hover state for zinc-750 (between 700 and 800) */
        .hover\\:bg-zinc-750:hover {
          background-color: rgb(55 55 60);
        }
      `}</style>
    </div>
  )
}
